import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import { Button } from './Button';
import { isAppError } from '@/lib/api/errors';

/** Skeleton shimmer for loading lists/detail. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-sm bg-canvas-deep-sand', className)} />;
}

export function LoadingRows({ rows = 6 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-xs" aria-busy aria-live="polite">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-[44px] w-full" />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-sm rounded-md border-[0.5px] border-dashed border-hairline-strong bg-canvas-bone p-3xl text-center">
      <p className="eyebrow text-ink-mute">{t('ui.nothingHereYet')}</p>
      <h3 className="text-title text-ink-deep">{title}</h3>
      {description && <p className="max-w-md text-small text-ink-mid">{description}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  const requestId = isAppError(error) ? error.requestId : undefined;
  const message = error instanceof Error ? error.message : t('ui.somethingWentWrong');
  return (
    <div className="flex flex-col items-center gap-sm rounded-md border-[0.5px] border-signal-error/40 bg-signal-error-fill p-2xl text-center">
      <p className="eyebrow text-signal-error">{t('ui.error')}</p>
      <p className="text-small text-ink-graphite">{message}</p>
      {onRetry && (
        <Button variant="ghost" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
      {requestId && (
        <details className="mt-xs">
          <summary className="cursor-pointer text-label text-ink-mute">{t('ui.reportThis')}</summary>
          <code className="tnum mt-2xs block select-all font-mono text-label text-ink-mute">
            request_id: {requestId}
          </code>
        </details>
      )}
    </div>
  );
}

export function ForbiddenBanner({ message }: { message?: string }) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="mb-md rounded-md border-s-[3px] border-signal-amber border-[0.5px] border-hairline bg-signal-amber-fill px-md py-sm text-small text-signal-amber-ink"
    >
      {message ?? t('ui.readOnlyAccess')}
    </div>
  );
}

/**
 * Standard four-state wrapper (spec §5.6): every list/detail composes this so loading,
 * empty, error, and forbidden are handled uniformly.
 */
export function DataView<T>({
  query,
  isEmpty,
  empty,
  forbidden,
  children,
}: {
  query: { isLoading: boolean; isError: boolean; error: unknown; data: T | undefined; refetch: () => void };
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  forbidden?: boolean;
  children: (data: T) => ReactNode;
}) {
  const { t } = useTranslation();
  if (forbidden) return <ForbiddenBanner />;
  if (query.isLoading) return <LoadingRows />;
  if (query.isError) {
    // A 403 surfaced as an error → read-only banner rather than a scary error card.
    if (isAppError(query.error) && query.error.is('forbidden')) return <ForbiddenBanner />;
    return <ErrorState error={query.error} onRetry={query.refetch} />;
  }
  if (query.data === undefined) return <LoadingRows />;
  if (isEmpty?.(query.data)) return <>{empty ?? <EmptyState title={t('ui.noResults')} />}</>;
  return <>{children(query.data)}</>;
}
